import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isImageDocument, isPdfDocument } from '@apis/documents';
import { useDocumentThumbnail } from '../../hooks/useDocumentBlob';
import { useObjectUrl } from '@hooks/useObjectUrl';
import { StatusPill } from '../../StatusPill/StatusPill';
import { VersionBadge } from '../../VersionBadge/VersionBadge';
import { FileTypeIcon } from '../../FileTypeIcon/FileTypeIcon';
import { RenameInput } from '../../RenameInput/RenameInput';
import { PendingSpinner } from '../../PendingSpinner/PendingSpinner';
import type { IFileItemProps } from '../itemProps';
import { useDragItem } from '../hooks/useDragItem';
import { formatProjectDate } from '@/utils/formatProjectDate';
import { selectSiteTz } from '@store/selectors/site.selectors';
import styles from './DocumentGrid.module.css';

export interface IFileCardProps extends IFileItemProps {
  /** Null while the working site resolves — no thumbnail can be asked for yet. */
  siteId: string | null;
}

/**
 * One document as a card: the preview with the version badge in the corner,
 * then name, date and status — the facts the table row shows, so switching
 * views loses nothing you'd act on. Same drag, right-click and inline-rename
 * contract as the row.
 *
 * The preview is the file's thumbnail when the backend has one, otherwise the
 * file-type glyph. Deliberately no full download and no pdf.js here: the
 * drawer renders ONE file that way; a folder renders fifty at once.
 *
 * A div, not a button: in rename mode it holds an input, which a button may
 * not contain. dnd-kit's attributes give it the button role and tab stop.
 */
export function FileCard({
  siteId, doc, isSelected, isMenuTarget = false, rename, isRejected, onSelect, onContextMenu,
}: IFileCardProps) {
  const { t } = useTranslation();
  const isRenaming = Boolean(rename);
  // A rename still saving: same lock as the table row.
  const isSaving = Boolean(doc.saving);
  const locked = isRenaming || isSaving;
  // Project zone, never the viewer's — same rule as the table row.
  const tz = useSelector(selectSiteTz);
  const { setNodeRef, attributes, listeners, style } = useDragItem(doc.id, 'document', locked);

  const previewable = isImageDocument(doc) || isPdfDocument(doc);
  const thumbnail = useDocumentThumbnail(siteId ?? '', doc.id, previewable && siteId !== null);
  const thumbnailUrl = useObjectUrl(thumbnail.data);
  const loadingThumbnail = previewable && siteId !== null && thumbnail.isPending;

  function toggle() {
    if (!locked) onSelect(isSelected ? null : doc.id);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.card} ${isSelected || isMenuTarget ? styles.cardSelected : ''} ${isRejected ? styles.dragRejected : ''} ${isSaving ? styles.cardSaving : ''}`}
      onClick={toggle}
      onContextMenu={(e) => (isSaving ? e.preventDefault() : onContextMenu(e, doc.id))}
      onKeyDown={(e) => {
        if (!locked && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          toggle();
        }
      }}
      {...attributes}
      {...listeners}
      // After the drag attributes: dnd-kit sets its own aria-pressed, and
      // selection is the state this control actually reports.
      aria-pressed={isSelected}
      aria-busy={isSaving || undefined}
      data-tour="documents-doc"
    >
      <span className={styles.preview}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" className={styles.thumbnail} />
        ) : loadingThumbnail ? (
          <span className={styles.thumbnailSkeleton} role="status" aria-label={t('documents.previewLoading')} />
        ) : (
          <FileTypeIcon doc={doc} size={40} />
        )}
        {/* Along the bottom edge; over a photo the band is a scrim, over a
            glyph it is transparent and the badge reads as it does in the row. */}
        <span className={`${styles.band} ${thumbnailUrl ? styles.bandOverImage : ''}`}>
          <VersionBadge version={doc.version} overlay={Boolean(thumbnailUrl)} />
        </span>
      </span>
      <span className={styles.caption}>
        <span className={`${styles.name} ${isRenaming ? styles.ghost : ''}`} title={doc.name}>{doc.name}{isSaving && <PendingSpinner />}</span>
        <span className={`${styles.metaRow} ${isRenaming ? styles.ghost : ''}`}>
          <span className={styles.meta}>{tz ? formatProjectDate(doc.updatedAt, tz) : '—'}</span>
          <StatusPill status={doc.status} />
        </span>
        {rename && (
          <span className={styles.renameOverlay}>
            <RenameInput {...rename} />
          </span>
        )}
      </span>
    </div>
  );
}
